import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import style from "components/url-desinger/social-network/social-network.module.scss";
import { DialogWrapper } from "components/shared/dialog";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { getPaymentOptionIcon } from "..";
import { PaymentOption, SetUrlBuilderData } from "store/url-builder";
import { PaymentProvider } from "enums/paymentProviders";
import { postPaymentOptions } from "adapters";

import * as yup from "yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "components/shared/input";

const schema = yup.object().shape({
  payments: yup.array().of(
    yup.object().shape({
      name: yup.string().required().trim().label("Payment Option"),
      url: yup.string().required().trim().label("URL"),
    })
  ),
});

export interface PaymentOptionsDialogProps {
  isOpen: boolean;
  onClose(): void;
  preselected?: PaymentOption[];
  builderType: string;
}

export const PaymentOptionsDialog = ({
  isOpen,
  onClose,
  preselected,
  builderType,
}: PaymentOptionsDialogProps) => {
  const dispatch = useDispatch();
  const { cardId }: { cardId: string } = useParams();
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { append, remove, fields } = useFieldArray({
    control,
    name: "payments",
  });

  const submit = (data: any) => {
    setLoading(true);

    if (Object.keys(errors).length) return;

    const payments = data.payments?.map((field: any) => {
      return {
        name: field.name,
        url: field.url,
      };
    });

    postPaymentOptions(cardId, builderType, payments)
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  const addNewPaymentOption = () => append({ name: "", url: "" });

  const removePaymentOption = (idx: number) => remove(idx);

  useEffect(() => {
    if (preselected && preselected.length > 0) {
      append(preselected);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselected]);

  return (
    <DialogWrapper
      title="Payment Options"
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      open={isOpen}
      onSubmit={handleSubmit(submit)}
      loading={loading}
    >
      {fields.map((payment, index) => (
        <Grid container item xs={12} className="mb-3" key={payment.id}>
          <Grid xs className="mr-2">
            <Controller
              control={control}
              name={`payments[${index}].name`}
              key={payment.id}
              render={({ field: { value, onChange } }) => (
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    Select Payment Option
                  </Typography>
                  <Select
                    classes={{ root: style.socialSelect }}
                    labelId="providers-label"
                    id="providers"
                    disabled={loading}
                    value={value}
                    error={errors.payments && !!errors?.payments[index]?.name}
                    onChange={onChange}
                  >
                    {Object.keys(PaymentProvider).map((key, index) => {
                      const option = (PaymentProvider as any)[key];
                      if (option === PaymentProvider.None) return null;
                      return (
                        <MenuItem value={option} key={index}>
                          <Box display="inline-flex" alignItems="center">
                            <Box marginRight="5px" display="inline-flex">
                              {getPaymentOptionIcon(option, {
                                width: "25px",
                                height: "25px",
                              })}
                            </Box>
                            {option}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText error>
                    {(errors?.payments &&
                      errors.payments[index]?.name?.message) ||
                      ""}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid xs className="ml-2">
            <Controller
              control={control}
              name={`payments[${index}].url`}
              key={payment.id}
              render={({ field: { value, onChange } }) => (
                <Input
                  label="URL"
                  error={errors.payments && !!errors?.payments[index]?.url}
                  helperText={
                    (errors?.payments &&
                      errors.payments[index]?.url?.message) ||
                    ""
                  }
                  fullWidth
                  disabled={loading}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid
            item
            container
            alignItems={
              errors.payments &&
              (!!errors?.payments[index]?.name ||
                !!errors?.payments[index]?.url)
                ? "center"
                : "flex-end"
            }
            xs={1}
          >
            <IconButton
              onClick={() => removePaymentOption(index)}
              className="mb-1"
            >
              <HighlightOffIcon color="error" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Grid item container xs={12} justifyContent="center">
        <Button onClick={addNewPaymentOption} style={{ textTransform: "none" }}>
          <AddIcon /> Add New Payment Option
        </Button>
      </Grid>
    </DialogWrapper>
  );
};
